import React, { useEffect, useState } from 'react';
import {
  Box, Card, CardContent, Typography, Grid, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Button, Snackbar, Alert, CircularProgress, Avatar
} from '@mui/material';
import RedeemIcon from '@mui/icons-material/Redeem';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import LocalAtmIcon from '@mui/icons-material/LocalAtm';

const API_URL = process.env.REACT_APP_API_URL;

function CandidateRewardsDashboard({ candidateId }) {
  const [totalPoints, setTotalPoints] = useState(0);
  const [history, setHistory] = useState([]);
  const [vouchers, setVouchers] = useState([]);
  const [redemptions, setRedemptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [redeeming, setRedeeming] = useState(null);

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line
  }, [candidateId]);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [pointsRes, vouchersRes, redemptionsRes] = await Promise.all([
        fetch(`${API_URL}/api/reward/points/${candidateId}`),
        fetch(`${API_URL}/api/reward/vouchers`),
        fetch(`${API_URL}/api/reward/redemptions/${candidateId}`)
      ]);
      const pointsData = await pointsRes.json();
      const vouchersData = await vouchersRes.json();
      const redemptionsData = await redemptionsRes.json();
      setTotalPoints(pointsData.total || 0);
      setHistory(pointsData.history || []);
      setVouchers(vouchersData.vouchers || []);
      setRedemptions(redemptionsData.redemptions || []);
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to load rewards data', severity: 'error' });
    }
    setLoading(false);
  };

  const handleRedeem = async (voucher) => {
    setRedeeming(voucher.id);
    try {
      const res = await fetch(`${API_URL}/api/reward/redeem`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ candidate_id: candidateId, voucher_id: voucher.id })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Redemption failed');
      setSnackbar({ open: true, message: 'Voucher redeemed! Check redemption history.', severity: 'success' });
      fetchAll();
    } catch (err) {
      setSnackbar({ open: true, message: err.message, severity: 'error' });
    }
    setRedeeming(null);
  };

  return (
    <Box sx={{ p: { xs: 1, md: 3 }, maxWidth: 1100, mx: 'auto' }}>
      <Typography variant="h4" fontWeight={800} mb={3} color="primary.main" textAlign="center">
        🎁 My Rewards & Points
      </Typography>
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Grid container spacing={3} mb={3} columns={12}>
            <Grid span={4}>
              <Card sx={{ bgcolor: '#e0f7fa', boxShadow: 3 }}>
                <CardContent>
                  <Typography variant="h6" color="text.secondary" gutterBottom>Total Points</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <EmojiEventsIcon color="primary" sx={{ fontSize: 40 }} />
                    <Typography variant="h3" fontWeight={700} color="primary.main">{totalPoints}</Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid span={4}>
              <Card sx={{ bgcolor: '#fffde7', boxShadow: 3 }}>
                <CardContent>
                  <Typography variant="h6" color="text.secondary" gutterBottom>Available Vouchers</Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {vouchers.length === 0 ? <Typography color="text.secondary">No vouchers</Typography> : vouchers.map(v => (
                      <Box key={v.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Avatar src={v.image_url} alt={v.name} sx={{ width: 32, height: 32, mr: 1 }} />
                        <Typography fontWeight={600}>{v.name}</Typography>
                        <Typography color="text.secondary" fontSize={14}>({v.required_points} pts)</Typography>
                        <Button
                          size="small"
                          variant="contained"
                          color="success"
                          startIcon={<RedeemIcon />}
                          disabled={totalPoints < v.required_points || v.quantity < 1 || redeeming === v.id}
                          onClick={() => handleRedeem(v)}
                        >
                          {redeeming === v.id ? <CircularProgress size={18} /> : 'Redeem'}
                        </Button>
                      </Box>
                    ))}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid span={4}>
              <Card sx={{ bgcolor: '#f3e5f5', boxShadow: 3 }}>
                <CardContent>
                  <Typography variant="h6" color="text.secondary" gutterBottom>Redemption History</Typography>
                  <Box sx={{ maxHeight: 120, overflowY: 'auto' }}>
                    {redemptions.length === 0 ? <Typography color="text.secondary">No redemptions</Typography> : redemptions.slice(0, 3).map(r => (
                      <Box key={r.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Avatar src={r.Voucher?.image_url} alt={r.Voucher?.name} sx={{ width: 28, height: 28, mr: 1 }} />
                        <Typography fontWeight={600}>{r.Voucher?.name}</Typography>
                        <Typography color="text.secondary" fontSize={14}>{new Date(r.claimed_at).toLocaleDateString()}</Typography>
                        <Typography color={r.status === 'pending' ? 'warning.main' : r.status === 'approved' ? 'success.main' : 'error.main'} fontSize={14}>{r.status}</Typography>
                      </Box>
                    ))}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <Typography variant="h6" fontWeight={700} mb={1} mt={2}>Reward Points History</Typography>
          <TableContainer component={Paper} sx={{ mb: 4 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Activity</TableCell>
                  <TableCell>Points</TableCell>
                  <TableCell>Source</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {history.length === 0 ? (
                  <TableRow><TableCell colSpan={4} align="center">No history</TableCell></TableRow>
                ) : history.map(r => (
                  <TableRow key={r.id}>
                    <TableCell>{new Date(r.created_at).toLocaleDateString()}</TableCell>
                    <TableCell>{r.source.charAt(0).toUpperCase() + r.source.slice(1)}</TableCell>
                    <TableCell>{r.points}</TableCell>
                    <TableCell>{r.source_id}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackbar(s => ({ ...s, open: false }))} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default CandidateRewardsDashboard; 